import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import { Clear, Close, Search } from "@material-ui/icons";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Field, Form, Formik } from "formik";
import { KeyboardDatePicker } from "formik-material-ui-pickers";
import moment from "moment";
import React, { useRef } from "react";
import * as Yup from "yup";
import { BirthSexOptions, IdentificationStatusOptions, RegistrationStatusOptions } from "../../Base/AdoptionConstants";
import FormikCheckboxField from "../../Base/Components/FormikCheckboxField";
import FormikSelectField from "../../Base/Components/FormikSelectField";
import FormikTextField from "../../Base/Components/FormikTextField";
import FormikAutocomplete from "../../Base/Components/FormikAutocomplete";
import NameFields from "../../Base/Components/NameFields";

import { getCities } from "../../Base/Services/CityService";
import { getHospitals } from "../../Base/Services/HospitalService";
const useStyles = makeStyles((theme) => ({
  formGroup: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  responsiveDialog: {
    [theme.breakpoints.down("xs")]: {
      margin: 0,
      maxWidth: "100%",
      width: "100%",
      height: "100%",
      maxHeight: "none",
      borderRadius: 0,
    },
  },
  compactTitle: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactActions: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactContent: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
    form: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
    },
  },
}));

export default function RegistrationSearchCriteria({ searchCriteria, setSearchCriteria }) {
  const classes = useStyles();
  const [advancedSearchOpen, setAdvancedSearchOpen] = React.useState(false);
  const basicCriteriaFormikRef = useRef();
  const advancedCriteriaFormikRef = useRef();
  const [cityList, setCityList] = React.useState([]);
  const [hospitalList, setHospitalList] = React.useState([]);

  React.useEffect(() => {
    getCities().then((citiesResponse) => setCityList(["Unknown / Other", ...citiesResponse]));
    getHospitals().then((hospitalsResponse) => setHospitalList(["Unknown / Other", ...hospitalsResponse]));
  }, []);

  function getFormValuesFromSearchCriteria(criteria) {
    const formValues = {
      birthParentFirstName: criteria?.birthParentFirstName ?? "",
      birthParentMiddleName: criteria?.birthParentMiddleName ?? "",
      birthParentLastName: criteria?.birthParentLastName ?? "",
      adoptiveParentFirstName: criteria?.adoptiveParentFirstName ?? "",
      adoptiveParentMiddleName: criteria?.adoptiveParentMiddleName ?? "",
      adoptiveParentLastName: criteria?.adoptiveParentLastName ?? "",
      adopteeFirstName: criteria?.adopteeFirstName ?? "",
      adopteeMiddleName: criteria?.adopteeMiddleName ?? "",
      adopteeLastName: criteria?.adopteeLastName ?? "",
      birthCity: criteria?.birthCity ?? "",
      birthHospital: criteria?.birthHospital ?? "",
      birthDateRangeStart: criteria?.birthDateRangeStart ?? null,
      birthDateRangeEnd: criteria?.birthDateRangeEnd ?? null,
      adoptionAgencyAttorney: criteria?.adoptionAgencyAttorney ?? "",
      userFriendlyId: criteria?.userFriendlyId ?? "",
      birthSex: criteria?.birthSex ?? [],
      registrationStatus: criteria?.registrationStatus ?? [],
      identificationStatus: criteria?.identificationStatus ?? [],
      locatorNumber: criteria?.locatorNumber ?? "",
      stateFileNumber: criteria?.stateFileNumber ?? "",
      includeInactive: criteria?.includeInactive ?? false,
    };
    return formValues;
  }

  React.useEffect(() => {
    // Keep the basic & advanced search criteria in sync when search criteria changes
    const formValues = getFormValuesFromSearchCriteria(searchCriteria);
    basicCriteriaFormikRef.current?.setValues(formValues);
    advancedCriteriaFormikRef.current?.setValues(formValues);
  }, [searchCriteria]);

  function handleSubmit(values) {
    const specifiedSearchCriteria = {};
    for (const searchCriteria in values) {
      const value = values[searchCriteria];
      // Only search on the values that are populated (e.g, avoid searching where last name is blank string)
      if (value !== null && value !== "" && values !== undefined) {
        specifiedSearchCriteria[searchCriteria] = value;
      }
    }

    if (specifiedSearchCriteria.birthDateRangeStart) {
      if (typeof specifiedSearchCriteria.birthDateRangeStart === "string") {
        specifiedSearchCriteria.birthDateRangeStart = moment(specifiedSearchCriteria.birthDateRangeStart);
      }
      specifiedSearchCriteria.birthDateRangeStart = specifiedSearchCriteria.birthDateRangeStart.format("YYYY-MM-DD");
    }
    if (specifiedSearchCriteria.birthDateRangeEnd) {
      if (typeof specifiedSearchCriteria.birthDateRangeEnd === "string") {
        specifiedSearchCriteria.birthDateRangeEnd = moment(specifiedSearchCriteria.birthDateRangeEnd);
      }
      specifiedSearchCriteria.birthDateRangeEnd = specifiedSearchCriteria.birthDateRangeEnd.format("YYYY-MM-DD");
    }

    setSearchCriteria(specifiedSearchCriteria);
    setAdvancedSearchOpen(false);
    return Promise.resolve();
  }
  return (
    <div>
      <Formik
        initialValues={getFormValuesFromSearchCriteria()}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          birthDateRangeStart: Yup.date().nullable(),
          birthDateRangeEnd: Yup.date()
            .nullable()
            .test("end-date-is-later-than-start", "The end date must be later than the start date", function (endDate) {
              if (!this.parent.birthDateRangeStart || !endDate) {
                return true;
              }
              return endDate > this.parent.birthDateRangeStart;
            }),
        })}
        innerRef={basicCriteriaFormikRef}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item md={12} lg={2} className={classes.formGroup}>
                <FormikTextField name="userFriendlyId" fullWidth label="ID" size="small" />
              </Grid>
              <Grid item md={12} lg={4} className={classes.formGroup}>
                <NameFields namePrefix="adoptee" size="small" includeMiddleName={false} labelPrefix="Adoptee" />
              </Grid>
              <Grid item md={12} lg={6} className={classes.formGroup}>
                <Grid container spacing={2}>
                  <Grid item sm={6}>
                    <FormikAutocomplete name="birthCity" fullWidth options={cityList} label="Birth City" size="small" />
                  </Grid>
                  <Grid item sm={6}>
                    <FormikAutocomplete
                      name="birthHospital"
                      fullWidth
                      options={hospitalList}
                      label="Birth Hospital"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={6} className={classes.formGroup}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      component={KeyboardDatePicker}
                      label={"Birth Date Range Start"}
                      name="birthDateRangeStart"
                      format="MM/DD/yyyy"
                      placeholder="MM/DD/YYYY"
                      style={{ marginBottom: 9 }}
                      fullWidth
                      maxDate={new Date()}
                      maxDateMessage="Invalid date of birth"
                      KeyboardButtonProps={{
                        "aria-label": "Search Birth Date Range Start",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      component={KeyboardDatePicker}
                      label={"Birth Date Range End"}
                      name="birthDateRangeEnd"
                      format="MM/DD/yyyy"
                      placeholder="MM/DD/YYYY"
                      style={{ marginBottom: 9 }}
                      fullWidth
                      maxDate={new Date()}
                      maxDateMessage="Invalid date of birth"
                      KeyboardButtonProps={{
                        "aria-label": "Search Birth Date Range End",
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={6} className={classes.formGroup}>
                <Grid container justify="flex-end" alignItems="flex-end" style={{ height: "100%", paddingBottom: 8 }}>
                  <Button
                    onClick={() => setAdvancedSearchOpen(true)}
                    color="secondary"
                    variant="outlined"
                    style={{ marginRight: 16 }}
                  >
                    Advanced Search <ExpandMoreIcon />
                  </Button>
                  <Button variant="contained" color="primary" type="submit">
                    <Search /> Search
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      <Formik
        initialValues={getFormValuesFromSearchCriteria()}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          birthDateRangeStart: Yup.date().nullable(),
          birthDateRangeEnd: Yup.date()
            .nullable()
            .test("end-date-is-later-than-start", "The end date must be later than the start date", function (endDate) {
              if (!this.parent.birthDateRangeStart || !endDate) {
                return true;
              }
              return endDate > this.parent.birthDateRangeStart;
            }),
        })}
        innerRef={advancedCriteriaFormikRef}
      >
        {(formik) => (
          <Dialog
            open={advancedSearchOpen}
            onClose={() => setAdvancedSearchOpen(false)}
            fullWidth={true}
            maxWidth="lg"
            aria-labelledby="advanced-search-title"
            aria-describedby="advanced-search-description"
            classes={{ paper: classes.responsiveDialog }}
            PaperProps={{
              component: "form",
              onSubmit: formik.handleSubmit,
            }}
          >
            <DialogTitle id="advanced-search-title" className={classes.compactTitle}>
              <span style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Advanced Search</span>
                <IconButton
                  size="small"
                  color="default"
                  onClick={() => setAdvancedSearchOpen(!advancedSearchOpen)}
                  aria-label="close-dialog"
                >
                  <Close />
                </IconButton>
              </span>
            </DialogTitle>
            <DialogContent id="advanced-search-description" className={classes.compactContent}>
              <Grid container>
                <Grid item md={12} lg={2} className={classes.formGroup}>
                  <label style={{ fontSize: 14 }}>ID</label>
                  <FormikTextField name="userFriendlyId" fullWidth label="ID" size="small" />
                </Grid>
                <Grid item md={12} lg={5} className={classes.formGroup}>
                  <label style={{ fontSize: 14 }}>Adoptee Name</label>
                  <NameFields namePrefix="adoptee" size="small" idPrefix="advanced" />
                </Grid>
                <Grid item md={12} lg={5} className={classes.formGroup}>
                  <label style={{ fontSize: 14 }}>Birth Location</label>
                  <Grid container spacing={2}>
                    <Grid item sm={6}>
                      <FormikAutocomplete
                        name="birthCity"
                        fullWidth
                        options={cityList}
                        label="Birth City"
                        size="small"
                        idPrefix="advanced"
                      />
                    </Grid>
                    <Grid item sm={6}>
                      <FormikAutocomplete
                        name="birthHospital"
                        fullWidth
                        options={hospitalList}
                        label="Birth Hospital"
                        size="small"
                        idPrefix="advanced"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item md={6} className={classes.formGroup}>
                  <label style={{ fontSize: 14 }}>Birth Parent Name</label>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={KeyboardDatePicker}
                        label={"Birth Date Range Start"}
                        id="advanced-birthDateRangeStart"
                        name="birthDateRangeStart"
                        format="MM/DD/yyyy"
                        placeholder="MM/DD/YYYY"
                        style={{ marginBottom: 8 }}
                        fullWidth
                        maxDate={new Date()}
                        maxDateMessage="Invalid date of birth"
                        KeyboardButtonProps={{
                          "aria-label": "Advanced Search Birth Date Range Start",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        component={KeyboardDatePicker}
                        label={"Birth Date Range End"}
                        id="advanced-birthDateRangeEnd"
                        name="birthDateRangeEnd"
                        format="MM/DD/yyyy"
                        placeholder="MM/DD/YYYY"
                        style={{ marginBottom: 8 }}
                        fullWidth
                        maxDate={new Date()}
                        maxDateMessage="Invalid date of birth"
                        KeyboardButtonProps={{
                          "aria-label": "Advanced Search Birth Date Range End",
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item className={classes.formGroup} md={4}>
                  <label style={{ fontSize: 14 }}>Adoption Agency/Attorney</label>
                  <FormikTextField
                    name="adoptionAgencyAttorney"
                    label="Adoption Agency/Attorney"
                    size="small"
                    idPrefix="advanced"
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item md={12} lg={6} className={classes.formGroup}>
                  <label style={{ fontSize: 14 }}>Birth Parent Name</label>
                  <NameFields namePrefix="birthParent" size="small" idPrefix="advanced" />
                </Grid>
                <Grid item md={12} lg={6} className={classes.formGroup}>
                  <label style={{ fontSize: 14 }}>Adoptive Parent Name</label>
                  <NameFields namePrefix="adoptiveParent" size="small" idPrefix="advanced" />
                </Grid>
              </Grid>
              <Grid container alignItems="flex-end">
                <Grid item md={4} sm={6} className={classes.formGroup}>
                  <FormikSelectField
                    name="birthSex"
                    options={BirthSexOptions}
                    getValueForOption={(option) => option.value}
                    getLabelForOption={(option) => option.label}
                    label="Birth Sex"
                    idPrefix="advanced"
                    multiple={true}
                  />
                </Grid>
                <Grid item md={4} sm={6} className={classes.formGroup}>
                  <FormikSelectField
                    name="registrationStatus"
                    options={RegistrationStatusOptions}
                    getValueForOption={(option) => option.value}
                    getLabelForOption={(option) => option.label}
                    label="Registration Status"
                    idPrefix="advanced"
                    multiple={true}
                  />
                </Grid>
                <Grid item md={4} sm={6} className={classes.formGroup}>
                  <FormikSelectField
                    name="identificationStatus"
                    options={IdentificationStatusOptions}
                    getValueForOption={(option) => option.value}
                    getLabelForOption={(option) => option.label}
                    label="Identification Status"
                    idPrefix="advanced"
                    multiple={true}
                  />
                </Grid>
                <Grid item sm={4} className={classes.formGroup}>
                  <FormikTextField
                    name="stateFileNumber"
                    fullWidth
                    label="State File Number"
                    size="small"
                    idPrefix="advanced"
                    onBlur={() => alert("This field is not implemented yet!")}
                  />
                </Grid>
                <Grid item sm={4} className={classes.formGroup}>
                  <FormikTextField
                    name="locatorNumber"
                    fullWidth
                    label="Locator Number"
                    size="small"
                    idPrefix="advanced"
                    onBlur={() => alert("This field is not implemented yet!")}
                  />
                </Grid>
                <Grid item sm={4} className={classes.formGroup}>
                  <FormikCheckboxField
                    name="includeInactive"
                    fullWidth
                    label="Include inactive records?"
                    size="small"
                    idPrefix="advanced"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={classes.compactActions}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  const defaultFormValues = getFormValuesFromSearchCriteria();
                  advancedCriteriaFormikRef.current?.setValues(defaultFormValues);
                }}
              >
                <Clear /> Clear
              </Button>
              <div style={{ flexGrow: 1 }} />
              <Button
                type="button"
                color="default"
                onClick={() => {
                  // Undo any changes to the search criteria when hitting cancel
                  const formValues = getFormValuesFromSearchCriteria(searchCriteria);
                  basicCriteriaFormikRef.current?.setValues(formValues);
                  advancedCriteriaFormikRef.current?.setValues(formValues);
                  setAdvancedSearchOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary">
                <Search /> Search
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Formik>
    </div>
  );
}
